"use client";

import {
  CHART_LINE_CURVE_TYPES,
  formatChartDetailDisplayValue,
  formatChartDisplayValue,
} from "@lattice/shared-frontend/charts";
import React, { useEffect, useMemo } from "react";
import {
  Line,
  LineChart as RechartsLineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import type { T_ChartData } from "@lattice/core-lib/types/chart";

import { ChartSeriesHeader } from "./ChartSeriesHeader";
import { CHART_X_AXIS_MIN_TICK_GAP } from "./chart-axis";
import {
  getPointDetailLabel,
  getPointLabel,
  getPointValue,
  isScanEligible,
} from "./chart-data";
import { resolveHeaderIndex } from "./chart-scan";
import { useChartScan } from "./use-chart-scan";
import "./charts-kit.css";

const AXIS_FONT_SIZE = 10;

export type T_LineChartProps = {
  chart: T_ChartData;
  /** Series stroke token; defaults to the positive financial token. */
  color?: string;
  /**
   * Derive stroke color and a header %-change readout from the series trend
   * (first vs. current point), mirroring the mobile line chart. An explicit
   * `color` prop still wins for callers that own trend logic (portfolio).
   */
  trendColoring?: boolean;
  /** Chart height in px (plot only). */
  height?: number;
  /** Notifies compositions that own the live-value header. */
  onScanPointChange?: (
    point: { index: number; label: string; value: number } | null,
  ) => void;
  /** Hide the built-in series header when a composition owns the readout. */
  showHeader?: boolean;
  /** Caller-known fabricated fallback data: disables scan. */
  syntheticFallback?: boolean;
  /** Optional series display name for the header (default "Value"). */
  valueName?: string;
};

/**
 * Single-series line chart — web mirror of the mobile LineChart line mode.
 * Layout per chart policy: header (legend IS the scan readout) → plot
 * (nothing overlays it). Controls (IntervalPills) are composed by callers
 * below this component.
 */
export function LineChart({
  chart,
  color,
  height = 220,
  onScanPointChange,
  showHeader = true,
  syntheticFallback,
  trendColoring = false,
  valueName = "Value",
}: T_LineChartProps) {
  const formatOptions = {
    valueFormat: chart.valueFormat,
    valuePrefix: chart.valuePrefix,
    valueSuffix: chart.valueSuffix,
  };
  const points = useMemo(
    () =>
      chart.data.map((point, index) => ({
        index,
        label: getPointLabel(point),
        detailLabel: getPointDetailLabel(point),
        value: getPointValue(point, "value"),
      })),
    [chart.data],
  );
  const scanEnabled = isScanEligible(chart, syntheticFallback);
  const { handlers, scanIndex } = useChartScan(points.length, scanEnabled);
  const headerIndex = resolveHeaderIndex(scanIndex, points.length);
  const headerPoint = headerIndex === null ? null : points[headerIndex];
  const hasNegative = points.some(
    (point) => point.value !== null && point.value < 0,
  );

  // Trend readout mirrors mobile: change is measured from the FIRST point of
  // the visible range to the header point (scanned or latest), and the line
  // color follows the range trend (last vs first).
  const firstValue = points.find((point) => point.value !== null)?.value ?? 0;
  const lastValue =
    [...points].reverse().find((point) => point.value !== null)?.value ?? 0;
  const trendPositive = lastValue >= firstValue;
  const strokeColor =
    color ??
    (trendColoring && !trendPositive
      ? "var(--lattice-financial-down)"
      : "var(--lattice-financial-positive)");
  const headerDelta = (() => {
    if (!trendColoring || headerPoint?.value == null) return undefined;
    if (Math.abs(firstValue) <= 0.0005) return undefined;
    const changePercent =
      ((headerPoint.value - firstValue) / Math.abs(firstValue)) * 100;
    const positive = headerPoint.value >= firstValue;
    return {
      positive,
      text: `${positive ? "+" : ""}${changePercent.toFixed(2)}%`,
    };
  })();

  // eslint-disable-next-line no-restricted-syntax -- Recharts scan state crosses into a caller-owned live header.
  useEffect(() => {
    if (!onScanPointChange) return;
    const point = scanIndex === null ? null : points[scanIndex];
    if (!point || point.value === null) {
      onScanPointChange(null);
      return;
    }
    onScanPointChange({
      index: point.index,
      label: point.label,
      value: point.value,
    });
  }, [onScanPointChange, points, scanIndex]);

  if (points.length === 0) {
    return (
      <div className="ck-root">
        <div className="ck-empty">No chart data</div>
      </div>
    );
  }

  return (
    <div className="ck-root">
      {chart.title ? (
        <div className="ck-title-row">
          <h3 className="ck-title">{chart.title}</h3>
        </div>
      ) : null}

      {showHeader && headerPoint ? (
        <ChartSeriesHeader
          entries={[
            {
              color: strokeColor,
              delta: headerDelta,
              name: valueName,
              value:
                headerPoint.value === null
                  ? "—"
                  : formatChartDetailDisplayValue(
                      headerPoint.value,
                      formatOptions,
                    ),
            },
          ]}
          label={headerPoint.detailLabel}
        />
      ) : null}

      <div
        className={scanEnabled ? "ck-plot ck-plot--scan" : "ck-plot"}
        style={{ height }}
      >
        <ResponsiveContainer height="100%" width="100%">
          <RechartsLineChart
            data={points}
            margin={{ bottom: 4, left: 0, right: 8, top: 8 }}
            {...handlers}
          >
            <XAxis
              axisLine={false}
              // Index-keyed axis so ReferenceLine x={scanIndex} addresses the
              // scale directly; a label-keyed category scale silently drops
              // numeric coordinates (and label-addressing breaks on repeated
              // formatted dates — the shipped mobile bug the policy bans).
              dataKey="index"
              interval="preserveStartEnd"
              minTickGap={CHART_X_AXIS_MIN_TICK_GAP}
              stroke="var(--lattice-chart-axis-label)"
              tick={{ fontSize: AXIS_FONT_SIZE }}
              tickFormatter={(value: number) => points[value]?.label ?? ""}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              domain={["auto", "auto"]}
              stroke="var(--lattice-chart-axis-label)"
              tick={{ fontSize: AXIS_FONT_SIZE }}
              tickFormatter={(value: number) =>
                formatChartDisplayValue(value, formatOptions)
              }
              tickLine={false}
              width={44}
            />
            {hasNegative ? (
              <ReferenceLine
                stroke="var(--lattice-chart-zero-line)"
                strokeWidth={1}
                y={0}
              />
            ) : null}
            {scanIndex !== null ? (
              <ReferenceLine
                stroke="var(--lattice-chart-zero-line)"
                strokeWidth={1.4}
                x={scanIndex}
              />
            ) : null}
            <Line
              activeDot={false}
              dataKey="value"
              dot={false}
              isAnimationActive={false}
              stroke={strokeColor}
              strokeWidth={2}
              type={CHART_LINE_CURVE_TYPES.web}
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
